import { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Divider,
  Box,
  Skeleton,
  Paper,
  Button,
  CircularProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import { green, grey } from "@mui/material/colors";
import { db } from "../firebase/config";
import { collection, getDocs } from "firebase/firestore";
import { Lock } from "@mui/icons-material";
import Logo from "../assets/img/benscogroup_512.png";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { doc, getDoc } from "firebase/firestore";

// Define a custom Material UI theme for a modern and clean look
const theme = createTheme({
  palette: {
    primary: {
      main: green[600], // A vibrant green for primary actions and accents
      light: green[300], // Lighter green for subtle accents
      dark: green[800], // Darker green for stronger emphasis
    },
    secondary: {
      main: grey[900], // Dark grey for general text and secondary elements
    },
    background: {
      default: grey[50], // A very light grey for the main page background
      paper: "#ffffff", // Pure white for cards and raised elements
    },
    text: {
      primary: grey[900], // Dark text for main content
      secondary: grey[700], // Lighter text for secondary information
    },
  },
  typography: {
    fontFamily: '"Inter", sans-serif', // Use Inter for a modern feel
    h4: {
      fontWeight: 700, // Bold for main headings
      fontSize: "2.2rem",
      "@media (max-width:600px)": {
        fontSize: "1.8rem", // Smaller on mobile
      },
    },
    h6: {
      fontWeight: 600, // Semi-bold for card titles
    },
    body1: {
      fontSize: "1rem",
    },
    body2: {
      fontSize: "0.875rem",
    },
  },
  shape: {
    borderRadius: 12, // Apply consistent, larger border radius globally
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12, // Ensure all cards have the theme's border radius
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 12, // Ensure selects have the theme's border radius
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12, // If buttons were used, they would get this
          textTransform: "none", // Disable uppercase transformation
          fontWeight: 600, // Make button text semi-bold
          padding: "8px 16px", // Standard padding
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 12, // Ensure skeletons match the rounded design
        },
      },
    },
  },
});

// Styled component for the summary cards, adding hover effects and distinct border
const SummaryCard = styled(Card)(({ theme }) => ({
  transition: theme.transitions.create(["transform", "box-shadow"], {
    duration: theme.transitions.duration.short, // Smooth transition on hover
  }),
  "&:hover": {
    transform: "translateY(-4px)", // Lift card slightly on hover
    boxShadow: theme.shadows[6], // More prominent shadow on hover
  },
  borderLeft: `6px solid ${theme.palette.primary.main}`, // Thicker, primary-colored left border
  height: "100%", // Ensure cards fill their grid space
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.shape.borderRadius, // Apply global border radius
  boxShadow: theme.shadows[3], // Default subtle shadow
}));

// Styled component for the KPI score text, with a vibrant gradient
const ScoreText = styled(Typography)(({ theme }) => ({
  fontWeight: 800, // Extra bold for the score
  fontSize: "3.5rem", // Large font size for impact
  background:
    theme.palette.mode === "light"
      ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
      : `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
  WebkitBackgroundClip: "text", // Clip background to text shape
  WebkitTextFillColor: "transparent", // Make text transparent to show background
  display: "inline-block", // Required for background-clip to work on text
}));

function KpiSummaryPage({ onLogin }) {
  const yearOptions = ["2025", "2026", "2027", "2028", "2029", "2030"];
  const [selectedYear, setSelectedYear] = useState("2025");
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [loginOpen, setLoginOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [loginData, setLoginData] = useState({
    username: "",
    password: "",
  });
  const [authLoading, setAuthLoading] = useState(false);

  const handleCardClick = (perusahaan, tahun) => {
    setSelectedCard({ perusahaan, tahun });
    setLoginOpen(true);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);

    try {
      const { username, password } = loginData;
      const userRef = doc(db, "users", username);
      const snap = await getDoc(userRef);

      if (!snap.exists()) {
        toast.error("User tidak ditemukan");
        setAuthLoading(false);
        return;
      }

      const data = snap.data();
      if (data.password !== password) {
        toast.error("Password salah");
        setAuthLoading(false);
        return;
      }

      toast.success("Login berhasil!");

      // Store user data in localStorage or context
      localStorage.setItem(
        "user",
        JSON.stringify({
          username: data.username,
          role: data.role,
          company: data.company || null,
        })
      );

      setTimeout(() => {
        setLoginOpen(false);
        setAuthLoading(false);
        navigate(`/kpi-list/${selectedCard.perusahaan}/${selectedCard.tahun}`, {
          state: { role: data.role },
        });
      }, 2000);
    } catch (err) {
      toast.error("Terjadi kesalahan saat login");
      setAuthLoading(false);
      console.error("Login error:", err);
    }
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    fetchSummary(selectedYear);
  }, [selectedYear]);

  const fetchSummary = async (year) => {
    setLoading(true);
    const summary = [];

    try {
      const perusahaanSnapshot = await getDocs(
        collection(db, "kpi_collections")
      );

      for (const perusahaanDoc of perusahaanSnapshot.docs) {
        const perusahaanName = perusahaanDoc.id;
        const tahun = `${year}`;
        const kpiRef = collection(db, "kpi_collections", perusahaanName, tahun);
        const kpiSnapshot = await getDocs(kpiRef);

        let totalScore = 0;

        kpiSnapshot.forEach((kpiDoc) => {
          const kpiData = kpiDoc.data();
          totalScore += parseFloat(kpiData.nilai) || 0;
        });

        if (!kpiSnapshot.empty) {
          summary.push({
            perusahaan: perusahaanName,
            tahun: year,
            total: totalScore.toFixed(2),
          });
        }
      }

      setSummaryData(summary);
    } catch (err) {
      console.error("Gagal mengambil data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    navigate("/login");
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          p: 2,
        }}
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Paper
            elevation={2}
            sx={{
              mb: 4,
              p: { xs: 2, sm: 3 },
              borderRadius: theme.shape.borderRadius,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 2,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{
                  width: 60,
                  height: 60,
                  bgcolor: "transparent",
                }}
                src={Logo}
                alt="Company Logo"
              />
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: theme.palette.primary.dark,
                }}
              >
                <span>Skor KPI Tahunan Bensco Group</span>
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel id="year-select-label">Tahun</InputLabel>
                <Select
                  labelId="year-select-label"
                  id="year-select"
                  value={selectedYear}
                  label="Tahun"
                  onChange={(e) => setSelectedYear(e.target.value)}
                  sx={{ borderRadius: theme.shape.borderRadius }}
                >
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                color="primary"
                startIcon={<Lock />}
                onClick={handleLogin}
              >
                Login
              </Button>
            </Box>
          </Paper>
          <>
            {loading ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                  }}
                >
                  <CircularProgress
                    size={60}
                    thickness={4}
                    sx={{ color: theme.palette.primary.main }}
                  />
                </Box>
                <Grid container spacing={3}>
                  {[1, 2, 3, 4, 5, 6].map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item}>
                      <Skeleton
                        variant="rectangular"
                        height={200}
                        sx={{ borderRadius: theme.shape.borderRadius }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            ) : summaryData.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 8,
                  textAlign: "center",
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: theme.shape.borderRadius,
                  boxShadow: theme.shadows[1],
                  mt: 3,
                  p: 3,
                }}
              >
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  Tidak ada data untuk tahun {selectedYear}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Silakan pilih tahun lain atau tambahkan data KPI
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {summaryData.map((item, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <SummaryCard
                      onClick={() =>
                        handleCardClick(item.perusahaan, item.tahun)
                      }
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          boxShadow: theme.shadows[8], // More prominent shadow on hover
                        },
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Typography variant="h6" component="h2" gutterBottom>
                          {item.perusahaan}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          paragraph
                        >
                          Tahun {item.tahun}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "center",
                            minHeight: 80,
                          }}
                        >
                          <ScoreText variant="h3">{item.total}</ScoreText>
                        </Box>
                      </CardContent>
                    </SummaryCard>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
          {/* Login Dialog */}
          <Dialog open={loginOpen} onClose={() => setLoginOpen(false)}>
            <DialogTitle>Login Required</DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Masukkan username dan password untuk melihat detail KPI{" "}
                {selectedCard?.perusahaan} tahun {selectedCard?.tahun}
              </Typography>
              <form onSubmit={handleLoginSubmit}>
                <TextField
                  margin="dense"
                  label="Username"
                  type="text"
                  fullWidth
                  variant="outlined"
                  name="username"
                  value={loginData.username}
                  onChange={handleLoginChange}
                  required
                  disabled={authLoading}
                />
                <TextField
                  margin="dense"
                  label="Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  name="password"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  required
                  disabled={authLoading}
                />
                <DialogActions>
                  <Button
                    onClick={() => setLoginOpen(false)}
                    disabled={authLoading}
                  >
                    Batal
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={authLoading}
                  >
                    {authLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Login"
                    )}
                  </Button>
                </DialogActions>
              </form>
            </DialogContent>
          </Dialog>
        </Container>
      </Box>
      <Toaster position="top-center" reverseOrder={false} />
    </ThemeProvider>
  );
}

export default KpiSummaryPage;
